/**
 * SB-Voranmeldung-Tab (Sprint 4 / Sprint 6, Feb 2026).
 *
 * SB-Variante der Voranmeldung-Page. Ruft die /api/advisor/clients/{cid}/voranmeldung/*-
 * Routen auf (mit Vollmacht-Audit-Log). UI-Layout entspricht der FL-Variante,
 * jedoch ohne „Self-Service"-Erklärungstexte.
 *
 * Verwendet ``advisorApi`` für Axios-Aufrufe (Bearer + Headers).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { downloadBlob, extractBlobError } from "@/utils/downloadBlob";
import { XmlPreflightDialog } from "@/components/XmlPreflightDialog";
import { PlausiWarnings } from "@/components/voranmeldung/PlausiWarnings";
import { advisorFocusUrl } from "@/utils/entityLinks";
import { PreflightChecklist } from "@/components/voranmeldung/PreflightChecklist";
import {
  ReceiptText, Download, FileCheck2, RefreshCw, AlertTriangle,
  ArrowDown, ArrowUp, Lock, Calendar, Pencil, Trash2,
} from "lucide-react";


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = { draft: "Entwurf", ready: "Prüfungsfertig", submitted: "Eingereicht" };
const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  ready: "bg-blue-100 text-blue-800 border-blue-200",
  submitted: "bg-emerald-100 text-emerald-800 border-emerald-200",
};


function fmtMoney(value) {
  if (value === null || value === undefined) return "—";
  return Number(value).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; }
}


function advisorHeaders() {
  const t = localStorage.getItem("advisor_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}


function PeriodPicker({ value, onChange, country }) {
  const currentYear = new Date().getFullYear();
  const years = useMemo(() => Array.from({ length: 7 }, (_, i) => currentYear - i), [currentYear]);
  const monthLabels = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
  // AT kennt keine Jahres-UVA — U30-Zeiträume sind nur monatlich/quartalsweise
  const isAT = (country || "").toUpperCase() === "AT";
  useEffect(() => {
    if (isAT && value.period_type === "yearly") {
      onChange({ ...value, period_type: "quarterly", period_index: 1 });
    }
  }, [isAT, value.period_type]);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="advisor-voranm-period-picker">
      <div>
        <Label className="text-xs text-slate-600 mb-1.5 block">Zeitraum-Typ</Label>
        <Select value={value.period_type} onValueChange={(v) => onChange({ ...value, period_type: v, period_index: v === "yearly" ? null : 1 })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="monthly">Monatlich</SelectItem>
            <SelectItem value="quarterly">Quartalsweise</SelectItem>
            {!isAT && <SelectItem value="yearly">Jährlich</SelectItem>}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-slate-600 mb-1.5 block">Jahr</Label>
        <Select value={String(value.year)} onValueChange={(v) => onChange({ ...value, year: Number(v) })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {value.period_type !== "yearly" && (
        <div>
          <Label className="text-xs text-slate-600 mb-1.5 block">{value.period_type === "monthly" ? "Monat" : "Quartal"}</Label>
          <Select value={String(value.period_index || 1)} onValueChange={(v) => onChange({ ...value, period_index: Number(v) })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {value.period_type === "monthly"
                ? monthLabels.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)
                : [1, 2, 3, 4].map((q) => <SelectItem key={q} value={String(q)}>Q{q}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}


function KennzahlenTable({ kennzahlen }) {
  if (!kennzahlen || kennzahlen.length === 0) {
    return <p className="text-sm text-slate-500 italic">Keine Kennzahlen für diese Periode.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="advisor-voranm-kennzahlen-table">
        <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="text-left py-2.5 px-3 w-20">KZ</th>
            <th className="text-left py-2.5 px-3">Bezeichnung</th>
            <th className="text-right py-2.5 px-3">Wert</th>
          </tr>
        </thead>
        <tbody>
          {kennzahlen.map((k) => (
            <tr key={k.code} className="border-b border-slate-100" data-testid={`advisor-voranm-kz-${k.code}`}>
              <td className="py-2 px-3 font-mono text-slate-900 font-medium">{k.code}</td>
              <td className="py-2 px-3 text-slate-700">
                {k.label}
                {k.is_tax_amount && <span className="ml-2 text-[10px] text-slate-400 uppercase">Steuer</span>}
              </td>
              <td className={`py-2 px-3 text-right font-mono tabular-nums ${k.is_tax_amount ? "font-semibold text-slate-900" : "text-slate-700"}`}>
                {fmtMoney(k.value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


export default function AdvisorVoranmeldungTab({ clientId, canEdit, clientCountry }) {
  const [period, setPeriod] = useState({
    period_type: "quarterly",
    year: new Date().getFullYear(),
    period_index: Math.floor(new Date().getMonth() / 3) + 1,
  });
  const [computation, setComputation] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [filings, setFilings] = useState([]);
  const [activeFiling, setActiveFiling] = useState(null);
  const [computing, setComputing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("compute");
  // Pre-Flight-Check (Juni 2026): Download läuft über eine Checklisten-Prüfung
  const [preflightFiling, setPreflightFiling] = useState(null);
  const fetchPreflight = useCallback(
    async (filing) => (
      await axios.get(`${API}/advisor/clients/${clientId}/voranmeldung/${filing.id}/preflight`, { headers: advisorHeaders() })
    ).data,
    [clientId],
  );
  const [reference, setReference] = useState("");

  const loadFilings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/advisor/clients/${clientId}/voranmeldung`, { headers: advisorHeaders() });
      setFilings(res.data.filings || []);
    } catch {
      // silent
    }
  }, [clientId]);

  useEffect(() => { loadFilings(); }, [loadFilings]);

  const handleCompute = async (overridePeriod) => {
    if (!canEdit) return toast.error("Vollmacht erforderlich");
    const p = overridePeriod || period;
    setComputing(true);
    setActiveFiling(null);
    setPreflight(null);
    try {
      const res = await axios.post(`${API}/advisor/clients/${clientId}/voranmeldung/compute`, p, { headers: advisorHeaders() });
      setComputation(res.data.computation);
      setPreflight(res.data.preflight || null);
      toast.success("Vorschau berechnet");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Berechnung fehlgeschlagen");
    } finally {
      setComputing(false);
    }
  };

  // Entwurf bearbeiten: Periode laden, in den Berechnen-Tab wechseln und neu berechnen.
  const editDraft = (f) => {
    const d = new Date(f.period_start);
    const year = d.getUTCFullYear();
    const month = d.getUTCMonth() + 1;
    const period_index = f.period_type === "monthly" ? month : Math.floor((month - 1) / 3) + 1;
    const newPeriod = { period_type: f.period_type, year, period_index };
    setPeriod(newPeriod);
    setComputation(null);
    setActiveTab("compute");
    handleCompute(newPeriod);
  };

  const handleDelete = async (f) => {
    if (!canEdit) return toast.error("Vollmacht erforderlich");
    if (!window.confirm(`Entwurf „${f.period_label}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/advisor/clients/${clientId}/voranmeldung/${f.id}`, { headers: advisorHeaders() });
      toast.success("Entwurf gelöscht");
      if (activeFiling?.id === f.id) { setActiveFiling(null); setComputation(null); }
      loadFilings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const handleSaveDraft = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/advisor/clients/${clientId}/voranmeldung`, period, { headers: advisorHeaders() });
      setActiveFiling(res.data.filing);
      toast.success(res.data.updated_existing ? "Bestehender Entwurf aktualisiert" : "Entwurf gespeichert");
      loadFilings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadXml = async (filing) => {
    try {
      const res = await axios.get(
        `${API}/advisor/clients/${clientId}/voranmeldung/${filing.id}/xml`,
        { headers: advisorHeaders(), responseType: "blob" },
      );
      downloadBlob(res, `${filing.country}_voranmeldung_${filing.period_label.replace(/\s/g, "_")}.xml`, "application/xml");
      toast.success("XML heruntergeladen");
    } catch (e) {
      toast.error(await extractBlobError(e, "Download fehlgeschlagen"));
    }
  };

  const handleSubmit = async () => {
    try {
      await axios.post(
        `${API}/advisor/clients/${clientId}/voranmeldung/${activeFiling.id}/mark-submitted`,
        { reference },
        { headers: advisorHeaders() },
      );
      toast.success("Als eingereicht markiert (im Auftrag des Mandanten)");
      setSubmitDialogOpen(false);
      setReference("");
      loadFilings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Markieren");
    }
  };

  const isSavedFiling = !!activeFiling;
  const showAuthority = computation?.country === "AT" ? "FinanzOnline" : "ELSTER";

  return (
    <div className="space-y-4" data-testid="advisor-voranmeldung-tab">
      {!canEdit && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
          <Lock className="h-3.5 w-3.5 inline mr-1.5" />
          Lesemodus — ohne Vollmacht keine Berechnung & Erstellung möglich.
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="compute" data-testid="advisor-voranm-tab-compute">
            <RefreshCw className="h-4 w-4 mr-1.5" />Berechnen
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="advisor-voranm-tab-history">
            <Calendar className="h-4 w-4 mr-1.5" />Verlauf ({filings.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="compute" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Periode wählen</CardTitle>
              <CardDescription>
                Engine zieht Rechnungen + Ausgaben des Mandanten aus dem gewählten Zeitraum
                und berechnet die Kennzahlen.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <PeriodPicker value={period} onChange={setPeriod} country={clientCountry} />
              <Button
                onClick={() => handleCompute()}
                disabled={computing || !canEdit}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="advisor-voranm-compute-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-1.5 ${computing ? "animate-spin" : ""}`} />
                {computing ? "Berechne…" : "Vorschau berechnen"}
              </Button>
            </CardContent>
          </Card>

          {computation && (
            <Card data-testid="advisor-voranm-result-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      {computation.country === "DE" ? "🇩🇪 UStVA" : "🇦🇹 UVA"} ·{" "}
                      <span className="text-slate-700 font-normal">
                        {fmtDate(computation.period_start)} – {fmtDate(computation.period_end)}
                      </span>
                    </CardTitle>
                    <CardDescription>
                      Steuerschema: <span className="font-mono">{computation.tax_scheme}</span> · {showAuthority}-konform
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {!isSavedFiling && (
                      <Button variant="outline" onClick={handleSaveDraft} disabled={saving} data-testid="advisor-voranm-save-draft-btn">
                        {saving ? "Speichere…" : "Als Entwurf speichern"}
                      </Button>
                    )}
                    {isSavedFiling && (
                      <>
                        <Button variant="outline" onClick={() => setPreflightFiling(activeFiling)} data-testid="advisor-voranm-download-xml-btn">
                          <Download className="h-4 w-4 mr-1.5" />XML
                        </Button>
                        {activeFiling.status !== "submitted" && (
                          <Button onClick={() => setSubmitDialogOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="advisor-voranm-mark-submitted-btn">
                            <FileCheck2 className="h-4 w-4 mr-1.5" />Als eingereicht
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                    <div className="text-xs text-slate-500">Umsätze netto</div>
                    <div className="text-lg font-semibold tabular-nums">{fmtMoney(computation.revenue_net)}</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                    <div className="text-xs text-slate-500">Umsatzsteuer</div>
                    <div className="text-lg font-semibold tabular-nums text-rose-700">+ {fmtMoney(computation.revenue_vat)}</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                    <div className="text-xs text-slate-500">Vorsteuer</div>
                    <div className="text-lg font-semibold tabular-nums text-emerald-700">− {fmtMoney(computation.input_vat)}</div>
                  </div>
                  <div className={`rounded-lg px-3 py-2 border-2 ${computation.payable_vat >= 0 ? "border-rose-300 bg-rose-50" : "border-emerald-300 bg-emerald-50"}`}>
                    <div className="text-xs text-slate-600 uppercase">{computation.payable_vat >= 0 ? "Zahllast" : "Erstattung"}</div>
                    <div className={`text-xl font-semibold tabular-nums ${computation.payable_vat >= 0 ? "text-rose-700" : "text-emerald-700"}`}>
                      {computation.payable_vat >= 0 ? <ArrowUp className="h-4 w-4 inline mr-1" /> : <ArrowDown className="h-4 w-4 inline mr-1" />}
                      {fmtMoney(Math.abs(computation.payable_vat))}
                    </div>
                  </div>
                </div>

                <PlausiWarnings
                  warnings={computation.warnings}
                  warningItems={computation.warning_items}
                  testId="advisor-voranm-warnings"
                  onOpenItem={(w) => window.open(advisorFocusUrl(clientId, w.entity_type, w.entity_id, period.year), "_blank", "noopener")}
                />

                {/* FinanzOnline/Elster Vorab-Check (schon in der Vorschau) */}
                <PreflightChecklist preflight={preflight} testId="advisor-voranm-preflight" />

                <div>
                  <h3 className="text-sm font-medium text-slate-900 mb-2">Kennzahlen ({showAuthority}-Format)</h3>
                  <KennzahlenTable kennzahlen={computation.kennzahlen} />
                </div>

                {isSavedFiling && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 space-y-1.5">
                    <div>
                      <Lock className="h-3.5 w-3.5 inline mr-1" />
                      Entwurf gespeichert (ID: <span className="font-mono">{activeFiling.id.slice(0, 8)}…</span>) — im Auftrag des Mandanten.
                    </div>
                    <div className="text-blue-800">
                      <strong>Hinweis:</strong> Das XML ist {showAuthority}-konform formatiert, aber nicht
                      kryptografisch signiert. Lade es in {showAuthority === "ELSTER" ? "Mein ELSTER (Importieren)" : "FinanzOnline (Datenstrom-Übermittlung)"} hoch.
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {filings.length === 0 ? (
                <div className="py-16 text-center text-sm text-slate-500" data-testid="advisor-voranm-history-empty">
                  <ReceiptText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  Noch keine Voranmeldungen für diesen Mandanten.
                </div>
              ) : (
                <table className="w-full text-sm" data-testid="advisor-voranm-history-table">
                  <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="text-left py-2.5 px-3">Periode</th>
                      <th className="text-left py-2.5 px-3">Land</th>
                      <th className="text-right py-2.5 px-3">Zahllast</th>
                      <th className="text-left py-2.5 px-3">Status</th>
                      <th className="text-left py-2.5 px-3">Eingereicht am</th>
                      <th className="text-right py-2.5 px-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filings.map((f) => (
                      <tr key={f.id} className="border-b border-slate-100" data-testid={`advisor-voranm-row-${f.id}`}>
                        <td className="py-2.5 px-3 font-medium text-slate-900">{f.period_label}</td>
                        <td className="py-2.5 px-3">{f.country === "DE" ? "🇩🇪 DE" : "🇦🇹 AT"}</td>
                        <td className={`py-2.5 px-3 text-right font-mono tabular-nums ${f.computation?.payable_vat >= 0 ? "text-rose-700" : "text-emerald-700"}`}>
                          {fmtMoney(f.computation?.payable_vat)}
                        </td>
                        <td className="py-2.5 px-3">
                          <Badge className={`${STATUS_STYLES[f.status]} border text-xs`}>
                            {STATUS_LABELS[f.status]}
                          </Badge>
                          {f.submitted_on_behalf && (
                            <span className="text-[10px] text-slate-500 ml-1.5">(SB-Vollmacht)</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-slate-600 text-xs">
                          {f.submitted_at ? fmtDate(f.submitted_at) : "—"}
                          {f.submission_reference && (
                            <div className="text-[10px] text-slate-400 font-mono">{f.submission_reference}</div>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-right space-x-1">
                          {f.status === "draft" && canEdit && (
                            <Button variant="ghost" size="sm" onClick={() => editDraft(f)} data-testid={`advisor-voranm-edit-${f.id}`} title="Entwurf bearbeiten">
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => setPreflightFiling(f)} data-testid={`advisor-voranm-download-${f.id}`}>
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          {f.status !== "submitted" && canEdit && (
                            <Button variant="ghost" size="sm" onClick={() => { setActiveFiling(f); setSubmitDialogOpen(true); }} data-testid={`advisor-voranm-submit-${f.id}`}>
                              <FileCheck2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {f.status === "draft" && canEdit && (
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(f)} data-testid={`advisor-voranm-delete-${f.id}`} title="Entwurf löschen"
                              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={submitDialogOpen} onOpenChange={(v) => !v && setSubmitDialogOpen(false)}>
        <DialogContent className="max-w-md" data-testid="advisor-voranm-submit-dialog">
          <DialogHeader>
            <DialogTitle>Als eingereicht markieren</DialogTitle>
            <DialogDescription>
              Wird mit „SB-Vollmacht"-Marker im Audit-Log dokumentiert.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-sm">Bestätigungsnummer (optional)</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="z.B. ELSTER-Telenummer / FinanzOnline-Ref"
              className="mt-1.5"
              data-testid="advisor-voranm-reference-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmitDialogOpen(false)}>Abbrechen</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSubmit} data-testid="advisor-voranm-confirm-submit">
              <FileCheck2 className="h-4 w-4 mr-1.5" />Bestätigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <XmlPreflightDialog
        filing={preflightFiling}
        onClose={() => setPreflightFiling(null)}
        fetchPreflight={fetchPreflight}
        onDownload={handleDownloadXml}
      />
    </div>
  );
}
